import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import ArrowOutwardRoundedIcon from '@mui/icons-material/ArrowOutwardRounded';
import { useLanguage } from '../contexts/LanguageContext';

const characterLimit = 500;

export default function ContactUs() {
  const { translations } = useLanguage();

  const [formValues, setFormValues] = React.useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });

  const [submitSuccess, setSubmitSuccess] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [characterCount, setCharacterCount] = React.useState(0);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newValue = e.target.value;

    if (newValue.length <= characterLimit) {
      setFormValues({
        ...formValues,
        [e.target.name]: newValue,
      });
      setCharacterCount(newValue.length);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formValues),
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      await response.json();
      setSubmitSuccess(true);
    } catch (error) {
      console.error('Error:', error);
      setSubmitSuccess(false);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container id="contact" sx={{ py: { xs: 8, sm: 14 } }}>
      <Card
        sx={{
          p: { xs: 3, sm: 5 },
          borderRadius: 6,
          background:
            'radial-gradient(circle at top right, rgba(124, 58, 237, 0.16), transparent 28%), linear-gradient(180deg, rgba(245, 243, 255, 0.88), rgba(255, 255, 255, 1))',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gap: { xs: 4, md: 5 },
            gridTemplateColumns: { xs: '1fr', md: 'minmax(260px, 0.75fr) minmax(0, 1fr)' },
          }}
        >
          <Stack spacing={2.5}>
            <Chip label={translations.contact_us} color="primary" variant="outlined" sx={{ alignSelf: 'flex-start' }} />
            <Typography component="h2" variant="h3" sx={{ maxWidth: 420 }}>
              {translations.contact_section.title}
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
              {translations.contact_section.description}
            </Typography>

            <Card
              variant="outlined"
              sx={{
                p: 2.5,
                borderRadius: 4,
                bgcolor: 'rgba(255,255,255,0.78)',
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box
                  sx={{
                    display: 'inline-flex',
                    p: 1.25,
                    borderRadius: 3,
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                  }}
                >
                  <MailOutlineRoundedIcon />
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                    ops@rewardiou.com
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                    {translations.contact_section.support_blurb}
                  </Typography>
                </Box>
              </Stack>
            </Card>

            {submitSuccess && (
              <Typography variant="subtitle2" sx={{ color: 'success.main' }}>
                {translations.contact_section.success_message}
              </Typography>
            )}
          </Stack>

          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              display: 'grid',
              gap: 2,
            }}
          >
            <TextField
              name="name"
              placeholder={translations.contact_section.name_placeholder}
              variant="outlined"
              fullWidth
              value={formValues.name}
              onChange={handleChange}
              required
              InputProps={{
                sx: {
                  borderRadius: 3,
                  bgcolor: 'rgba(255,255,255,0.82)',
                },
              }}
            />
            <TextField
              name="email"
              placeholder={translations.contact_section.email_placeholder}
              type="email"
              variant="outlined"
              fullWidth
              value={formValues.email}
              onChange={handleChange}
              required
              InputProps={{
                sx: {
                  borderRadius: 3,
                  bgcolor: 'rgba(255,255,255,0.82)',
                },
              }}
            />
            <TextField
              name="subject"
              placeholder={translations.contact_section.subject_placeholder}
              variant="outlined"
              fullWidth
              value={formValues.subject}
              onChange={handleChange}
              required
              InputProps={{
                sx: {
                  borderRadius: 3,
                  bgcolor: 'rgba(255,255,255,0.82)',
                },
              }}
            />
            <TextField
              name="message"
              placeholder={translations.contact_section.message_placeholder}
              multiline
              rows={7}
              variant="outlined"
              fullWidth
              value={formValues.message}
              onChange={handleChange}
              required
              InputProps={{
                sx: {
                  borderRadius: 3,
                  bgcolor: 'rgba(255,255,255,0.82)',
                  alignItems: 'flex-start',
                },
              }}
            />
            <Typography variant="caption" color="text.secondary">
              {characterCount}/{characterLimit} {translations.contact_section.characters}
            </Typography>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isLoading}
              endIcon={isLoading ? <CircularProgress size={18} color="inherit" /> : <ArrowOutwardRoundedIcon />}
              sx={{
                justifyContent: 'space-between',
                px: 2.25,
                py: 1.4,
                borderRadius: 3,
                fontSize: '1rem',
              }}
            >
              {translations.contact_section.send_button}
            </Button>
          </Box>
        </Box>
      </Card>
    </Container>
  );
}
